import { clamp } from '../core/math';
import type { Vec3 } from '../geo/types';
const EPSILON = 1e-8;
export type Cubic = readonly [number, number, number, number];
type Segment = readonly [Cubic, Cubic, Cubic];

export interface Curve {
  readonly segments: readonly Segment[];
  readonly knots: Float64Array;
}

export function pointAt(points: readonly Vec3[], index: number): Vec3 {
  const point = points[index];
  if (!point) throw new Error('Tour curve point is missing.');
  return point;
}

function reflected(endpoint: Vec3, neighbor: Vec3): Vec3 {
  return [
    2 * endpoint[0] - neighbor[0],
    2 * endpoint[1] - neighbor[1],
    2 * endpoint[2] - neighbor[2],
  ];
}

function knotSpan(a: Vec3, b: Vec3): number {
  return Math.sqrt(Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]));
}

function cubic(
  p0: number,
  p1: number,
  p2: number,
  p3: number,
  h0: number,
  h1: number,
  h2: number
): Cubic {
  const m1 = ((p1 - p0) / h0 - (p2 - p0) / (h0 + h1) + (p2 - p1) / h1) * h1;
  const m2 = ((p2 - p1) / h1 - (p3 - p1) / (h1 + h2) + (p3 - p2) / h2) * h1;
  return [p1, m1, 3 * (p2 - p1) - 2 * m1 - m2, 2 * (p1 - p2) + m1 + m2];
}

export function curveKnots(points: readonly Vec3[], linear: boolean): Float64Array {
  const knots = new Float64Array(points.length);
  let length = 0;
  for (let i = 1; i < points.length; i++) {
    const span = knotSpan(pointAt(points, i - 1), pointAt(points, i));
    length += Math.max(EPSILON, linear ? span * span : span);
    knots[i] = length;
  }
  for (let i = 1; i < points.length; i++) knots[i] = (knots[i] ?? 0) / length;
  return knots;
}

export function buildCurve(
  points: readonly Vec3[],
  knots: Float64Array,
  linear = false,
  closed = false
): Curve {
  const segments: Segment[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = pointAt(points, i);
    const p2 = pointAt(points, i + 1);
    if (linear) {
      segments.push([
        [p1[0], p2[0] - p1[0], 0, 0],
        [p1[1], p2[1] - p1[1], 0, 0],
        [p1[2], p2[2] - p1[2], 0, 0],
      ]);
      continue;
    }
    const end = points.length - 1;
    const p0 =
      i === 0 ? (closed ? pointAt(points, end - 1) : reflected(p1, p2)) : pointAt(points, i - 1);
    const p3 =
      i + 2 === points.length
        ? closed
          ? pointAt(points, 1)
          : reflected(p2, p1)
        : pointAt(points, i + 2);
    const h1 = (knots[i + 1] ?? 0) - (knots[i] ?? 0);
    const h0 =
      i === 0
        ? closed
          ? (knots[end] ?? 0) - (knots[end - 1] ?? 0)
          : h1
        : (knots[i] ?? 0) - (knots[i - 1] ?? 0);
    const h2 =
      i + 2 === points.length
        ? closed
          ? (knots[1] ?? 0)
          : h1
        : (knots[i + 2] ?? 0) - (knots[i + 1] ?? 0);
    segments.push([
      cubic(p0[0], p1[0], p2[0], p3[0], h0, h1, h2),
      cubic(p0[1], p1[1], p2[1], p3[1], h0, h1, h2),
      cubic(p0[2], p1[2], p2[2], p3[2], h0, h1, h2),
    ]);
  }
  return { segments, knots };
}

export function evaluate(c: Cubic, t: number): number {
  return ((c[3] * t + c[2]) * t + c[1]) * t + c[0];
}

export function lowerIndex(values: Float64Array, value: number): number {
  let low = 0;
  let high = values.length - 1;
  while (low + 1 < high) {
    const middle = (low + high) >>> 1;
    if ((values[middle] ?? 0) < value) low = middle;
    else high = middle;
  }
  return low;
}

export function curvePoint(curve: Curve, parameter: number): Vec3 {
  const index = lowerIndex(curve.knots, parameter);
  const segment = curve.segments[index];
  if (!segment) throw new Error('Tour curve has no segments.');
  const from = curve.knots[index] ?? 0;
  const t = clamp((parameter - from) / ((curve.knots[index + 1] ?? 1) - from), 0, 1);
  return [evaluate(segment[0], t), evaluate(segment[1], t), evaluate(segment[2], t)];
}

export function curveBearing(curve: Curve, parameter: number, fallback: number): number {
  const index = lowerIndex(curve.knots, parameter);
  const segment = curve.segments[index];
  if (!segment) return fallback;
  const from = curve.knots[index] ?? 0;
  const t = clamp((parameter - from) / ((curve.knots[index + 1] ?? 1) - from), 0, 1);
  const dx = (3 * segment[0][3] * t + 2 * segment[0][2]) * t + segment[0][1];
  const dz = (3 * segment[2][3] * t + 2 * segment[2][2]) * t + segment[2][1];
  return Math.hypot(dx, dz) < EPSILON ? fallback : Math.atan2(-dx, -dz);
}
